import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { safeNext } from "./LoginPage";
import { G_DARK, globalStyles } from "../components/Shared";

/**
 * Where the Keycloak callback lands: `/login/complete#token=<portal JWT>&next=<path>`.
 *
 * Not `/auth/complete`: `nginx.conf` proxies `/auth/` to the backend, so the
 * SPA never sees that address.
 *
 * The token travels in the fragment because a fragment never reaches a server
 * or an ingress log. It still reaches the address bar, and from there the
 * history, a bookmark or a screenshot, so the first thing this page does is
 * take it out with `history.replaceState`. `next` is honoured only when
 * `safeNext` accepts it — the backend checks it too, but this page is where
 * the browser actually goes, and an open redirect here would carry the
 * portal's name.
 *
 * Public and drawn outside Layout, so it injects `globalStyles` itself
 * (`publicRoutesStyles.test.jsx`).
 */

// Three base64url parts. Not a validation — `/auth/me` is that — but enough to
// refuse storing something that could never be a token.
const JWT_SHAPE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

function readFragment() {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  return { token: params.get("token"), next: params.get("next") };
}

export default function LoginCompletePage() {
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  // Read once, in the initializer, before the effect below clears it.
  const [fragment] = useState(readFragment);
  const [failed, setFailed] = useState(false);
  // StrictMode runs effects twice in development; the token must be taken once.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (window.location.hash) {
      window.history.replaceState(window.history.state, "",
                                  window.location.pathname + window.location.search);
    }
    const { token, next } = fragment;
    if (!token || !JWT_SHAPE.test(token)) { setFailed(true); return; }
    loginWithToken(token)
      .then(() => navigate(safeNext(next), { replace: true }))
      .catch(() => setFailed(true));
  }, [fragment, loginWithToken, navigate]);

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", padding: 24 }}>
      <style>{globalStyles}</style>
      <div style={{ maxWidth: 400, width: "100%", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, padding: 28, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
        <h1 style={{ margin: "0 0 12px", fontSize: 20, fontWeight: 700, color: "#1a1a1a" }}>Project Portal</h1>
        {failed ? (
          <>
            <div role="alert" style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13, marginBottom: 16 }}>
              The sign-in did not complete: the portal received no valid session from it. Please sign in again.
            </div>
            <Link to="/login" style={{ fontSize: 14, color: G_DARK, fontWeight: 500 }}>
              Back to the sign-in page
            </Link>
          </>
        ) : (
          <p role="status" style={{ margin: 0, fontSize: 14, color: "#374151" }}>Signing you in…</p>
        )}
      </div>
    </div>
  );
}
